import { add, addHours, addMinutes, format, isBefore, isSameYear, parse, startOfDay } from 'date-fns'
import { ko } from 'date-fns/locale'
import { fromZonedTime } from 'date-fns-tz'

import type { ShareAbleTime } from '@/api/client/purchase/dto/purchaseDto'

export type DateOption = {
  value: Date
  text: string
}

export const HALF_HOUR = 30 * 60 * 1000

export const yyyyMMdd_EEE = 'yyyy.MM.dd(EEE)'
export const yyMMdd_EEE = 'yy.MM.dd(EEE)'
export const MMdd_EEE = 'MM.dd(EEE)'
export const yyMMdd_EEE_HHmm = 'yy.MM.dd(EEE) HH:mm'
export const yyyyMMdd_HHmmss_dash = 'yyyy-MM-dd HH:mm:ss'
export const yyMMdd_HHmm = 'yy.MM.dd HH:mm'
export const yyMMdd = 'yy.MM.dd'
export const yyyyMMdd_dash = 'yyyy-MM-dd'

const allowanceTime = 10

export const generateParkingEndTime = (time: ShareAbleTime['usingTime']) => {
  const now = new Date()
  const endTime = format(new Date(now.getTime() + (time + allowanceTime) * 60000), 'HH:mm')
  return `${endTime}까지`
}

export const getModifiedDate = (dateString: string, formatString: string = yyyyMMdd_HHmmss_dash) => {
  const date = parse(dateString, formatString, new Date(), { locale: ko })
  const result = format(date, yyyyMMdd_EEE, { locale: ko })
  return result
}

export const formattingDate = (date: string | null) => {
  if (date == null) return '-'
  const dateObj = new Date(date)
  const formattedDate = format(dateObj, yyMMdd_EEE, { locale: ko })
  const formattedTime = dateObj.toTimeString().split(' ')[0]
  return `${formattedDate} ${formattedTime}`
}

export const formattingDateMMdd = (date: string | null, formatString: string = yyMMdd_EEE_HHmm) => {
  if (date == null) return '-'
  const dateObj = new Date(date)
  const formattedDateTime = format(dateObj, formatString, { locale: ko })
  return formattedDateTime
}

export const formatTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours === 0) {
    return `${remainingMinutes}분`
  } else if (remainingMinutes === 0) {
    return `${hours}시간`
  } else {
    return `${hours}시간 ${remainingMinutes}분`
  }
}

export const generateDates = (startDate: Date, dateTotalCount: number) => {
  const dates = []

  for (let i = 0; i < dateTotalCount; i++) {
    const oneDate = add(startDate, { days: i })
    const computedDateFormat = isSameYear(oneDate, startDate) ? MMdd_EEE : yyMMdd_EEE
    dates.push({
      value: oneDate,
      text: format(oneDate, computedDateFormat, { locale: ko })
    })
  }

  return dates
}

export const generateHalfHourIntervals = (startDate: Date) => {
  const hours = []

  const startTime =
    startDate.getMinutes() % 30 === 0 ? startDate : addMinutes(startDate, 30 - (startDate.getMinutes() % 30))
  const endTime = addHours(startOfDay(startDate), 24)

  let currentTime = startTime
  while (isBefore(addMinutes(currentTime, 30), endTime)) {
    const nextTime = addMinutes(currentTime, 30)
    const currentSlot = `${format(currentTime, 'HH:mm')}~${format(nextTime, 'HH:mm')}`
    hours.push(currentSlot)
    currentTime = nextTime
  }
  hours.push('23:30 ~ 24:00')

  return hours
}

export const generateTimeOption = (date: string) => {
  if (!date) return '-'
  const dateParts = date.split(' ')
  const dateOnly = dateParts[0]
  const timeOnly = dateParts[1]
  const dateOption = format(new Date(dateOnly), MMdd_EEE, { locale: ko })

  const timeOption =
    timeOnly +
    ' ~ ' +
    String(addMinutes(new Date(date), 30).getHours()).padStart(2, '0') +
    ':' +
    String(addMinutes(new Date(date), 30).getMinutes()).padStart(2, '0')
  return `${dateOption} ${timeOption}`
}

export const convertToUtc = (kstDate: Date | string): Date => {
  return fromZonedTime(new Date(kstDate), 'Asia/Seoul')
}
